import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { AuditValueName } from './AuditDetails.style';
import { Response200Type } from 'src/api_openapi/generated/openapi_proxy_staff_audit_event';
import { assertNever } from 'src_common/common/assertNever';

type RenderEventDetailsTableRowType = Array<null | JSX.Element>;

type InsideRenderEventDetailsType = null | JSX.Element | RenderEventDetailsTableRowType;

class AuditDetailsTradingState {
    public constructor(private readonly modelsState: ModelsState) {}

    // This function has to be rewrite after migration whole data for new trading
    private renderEventDetailsTableRow(
        audits: AuditType | AuditBodyType,
        toExclude: Array<string>,
        levelKey: Array<string> = [],
        styleClassName = 'none'
    ): RenderEventDetailsTableRowType {
        const keyArr: Array<string> = [...levelKey];
        // TODO: This class need refactor
        // @ts-expect-error
        return Object.keys(audits).map((key: string): InsideRenderEventDetailsType => {
            if (toExclude.includes(key) === false) {
                // @ts-expect-error
                const auditsKey = audits[key];

                if (auditsKey === null || auditsKey === undefined) {
                    return null;
                }
                if (auditsKey.constructor === Object) {
                    keyArr.push(key);

                    return this.renderEventDetailsTableRow(auditsKey, toExclude, keyArr, styleClassName);
                }

                if (Array.isArray(auditsKey) === true) {
                    if (auditsKey.length === 0) {
                        return null;
                    }

                    return (
                        <tr
                            className={styleClassName}
                            key={key}
                        >
                            <AuditValueName>{key}</AuditValueName>
                            <td>
                                <td>
                                    {auditsKey.map(
                                        // @ts-expect-error
                                        (audit: string, keyArr: Array<string>) => {
                                            const firstValue = JSON.stringify(audit).replace(/"/g, '');
                                            // @ts-expect-error
                                            const secondValue = keyArr == audits[key].length - 1 ? '' : ', ';
                                            return firstValue + secondValue;
                                        }
                                    )}
                                </td>
                            </td>
                        </tr>
                    );
                }

                const renderKey = keyArr.join('.');
                return (
                    <tr
                        className={styleClassName}
                        key={key}
                    >
                        {keyArr.length > 0 ? (
                            <AuditValueName>{`${renderKey}.${key}`}</AuditValueName>
                        ) : (
                            <AuditValueName>{key}</AuditValueName>
                        )}
                        <td>
                            {typeof auditsKey === 'number' &&
                            (/betLimits/.exec(renderKey) !== null || /cashout/i.exec(key)) !== null
                                ? auditsKey / 100
                                : typeof auditsKey === 'string' || auditsKey === null
                                  ? auditsKey
                                  : JSON.stringify(auditsKey)}
                        </td>
                    </tr>
                );
            }
            return null;
        });
    }

    private parseStringData(auditsBody: AuditType): AuditBodyType {
        if (auditsBody.body.selections !== undefined && auditsBody.body.selections !== null) {
            auditsBody.body.selections = JSON.parse(auditsBody.body.selections);
        }
        if (auditsBody.body.participants !== undefined && auditsBody.body.participants !== null) {
            auditsBody.body.participants = JSON.parse(auditsBody.body.participants);
        }
        return auditsBody.body;
    }

    private updateAuditsWhoData(userId: number, audits: AuditType): AuditType {
        if (userId === 0) {
            return audits;
        }

        const accountData = this.modelsState.getUserModel(userId).basicStaffData.data;

        const updatedAudits = {
            ...audits,
            who: {
                ...audits.who,
                name: accountData?.name,
                type: accountData?.type,
            },
        };

        return updatedAudits;
    }

    private renderChangeTitle(category: 'EventChange' | 'MarketChange' | 'SelectionChange'): string {
        switch (category) {
            case 'EventChange':
                return 'Event Change';
            case 'MarketChange':
                return 'Market Change';
            case 'SelectionChange':
                return 'Selection Change';

            default:
                return assertNever('audit category', category);
        }
    }

    private getAuditCategory(
        category: 'EventChange' | 'MarketChange' | 'SelectionChange'
    ): 'event' | 'market' | 'selections' | undefined {
        switch (category) {
            case 'EventChange':
                return 'event';
            case 'MarketChange':
                return 'market';
            case 'SelectionChange':
                return 'selections';

            default:
                return assertNever('audit category', category);
        }
    }

    public renderEventDetailsTable(audits: AuditType, id: string): JSX.Element | null {
        const toExclude = ['market', 'event', 'selections', 'body'];
        const tradingContentCategory = id.split('_');
        const autydType = tradingContentCategory[tradingContentCategory.length - 1]; // its return event/market/selection-change-number
        const audytTypeSplit = autydType === undefined ? ['-'] : autydType.split('-');

        const auditId = audytTypeSplit[audytTypeSplit.length - 1];
        const auditCategory = this.getAuditCategory(audits.category);

        if (auditCategory === undefined) {
            return null;
        }

        for (let i = 0; i < toExclude.length; i++) {
            // @ts-expect-error
            if (auditCategory.includes(toExclude[i].slice(0, 5))) {
                toExclude.splice(i, 1);
            }
        }

        const updatedAudits = this.updateAuditsWhoData(audits.who.id, audits);
        const auditsSelectionsParsed =
            updatedAudits.body.selections !== null && updatedAudits.body.selections !== undefined
                ? JSON.parse(updatedAudits.body.selections)
                : updatedAudits.body;

        const isAuditTopicNewTradingContentOrAuditIdEmpty =
            audits.topic === 'new_trading_content' || auditId === undefined;
        return (
            <table>
                <thead>
                    <td>
                        <strong>{this.renderChangeTitle(audits.category)}</strong>
                    </td>
                    <td>
                        <strong>Value</strong>
                    </td>
                </thead>
                <tbody>
                    {auditCategory === 'selections'
                        ? this.renderEventDetailsTableRow(
                              isAuditTopicNewTradingContentOrAuditIdEmpty
                                  ? auditsSelectionsParsed
                                  : { ...auditsSelectionsParsed[auditId] },
                              toExclude
                          )
                        : this.renderEventDetailsTableRow(this.parseStringData(updatedAudits), toExclude)}
                    <tr>
                        <td></td>
                        <td></td>
                    </tr>
                    <tr>
                        <td></td>
                        <td></td>
                    </tr>
                    {this.renderEventDetailsTableRow(updatedAudits, toExclude, [], 'technical-details')}
                </tbody>
            </table>
        );
    }
}

type AuditType = Response200Type['results'][number];
type AuditBodyType = Response200Type['results'][number]['body'];

interface PropsType {
    audits: AuditType;
    id: string;
}

export const AuditDetailsTradingNew = observer((props: PropsType) => {
    const appState = useAppStateContext();
    const [state] = useState(() => new AuditDetailsTradingState(appState.models));
    const { audits, id } = props;

    return <div className='audit-details-trading'>{state.renderEventDetailsTable(audits, id)}</div>;
});
